import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Heart, Package, Stethoscope, Gift, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockData } from '../mock';

const Donate = () => {
  const { donationItems } = mockData;
  const [currentSlide, setCurrentSlide] = useState(0);

  const categoryIcons = {
    "Pet Food": Heart,
    "Pet Supplies": Package,
    "Medical & Hygiene": Stethoscope,
    "Other / Household & Donations": Gift
  };

  const categoryColors = {
    "Pet Food": { bg: 'bg-red-100', text: 'text-red-600', icon: 'text-red-600' },
    "Pet Supplies": { bg: 'bg-olive-100', text: 'text-olive-600', icon: 'text-olive-600' },
    "Medical & Hygiene": { bg: 'bg-amber-100', text: 'text-amber-600', icon: 'text-amber-600' },
    "Other / Household & Donations": { bg: 'bg-mauve-200', text: 'text-mauve-700', icon: 'text-mauve-700' }
  };

  // Auto-rotate featured items every 2 weeks
  const getFeaturedItems = () => {
    const startDate = new Date('2025-01-01');
    const today = new Date();
    const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
    const cycleNumber = Math.floor(daysSinceStart / 14);
    
    const featured = [];
    donationItems.forEach((category) => {
      const itemCount = category.items.length;
      if (itemCount > 0) {
        const itemIndex = (cycleNumber * 2) % itemCount;
        const item = category.items[itemIndex];
        featured.push({ item: item, category: category.category });
      }
    });
    
    while (featured.length < 6) {
      donationItems.forEach((category) => {
        if (featured.length < 6) {
          const itemCount = category.items.length;
          const itemIndex = ((cycleNumber * 2) + 1 + featured.length) % itemCount;
          const item = category.items[itemIndex];
          featured.push({ item: item, category: category.category });
        }
      });
    }
    
    return featured.slice(0, 6);
  };

  const featuredItems = getFeaturedItems();
  const itemsPerSlide = 2;
  const totalSlides = Math.ceil(featuredItems.length / itemsPerSlide);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(timer);
  }, [totalSlides]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

  const currentItems = featuredItems.slice(
    currentSlide * itemsPerSlide,
    currentSlide * itemsPerSlide + itemsPerSlide
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-mauve-100 to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-darkslate-700 mb-4" data-testid="ready-to-donate">
            Ready to Donate?
          </h2>
          <p className="text-lg text-darkslate-500 mb-8">
            Your generosity helps keep pets with their families and cared for.
          </p>
          <p className="text-lg text-darkslate-500 mb-8">
            Contact us to arrange your donation. Every item makes a difference in keeping pets cared for.
          </p>
          <a 
            href="https://docs.google.com/forms/d/e/1FAIpQLSeT52gzuc0HqPFJ2u4FdMVN9TrRnM_kQGXoRKoYqrqx_Fuj-Q/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            data-testid="donation-form-link"
          >
            Click here to fill out our donation form
          </a>
        </div>
      </section>

      {/* Wishlist Essentials Carousel */}
      <section className="py-12 px-4 bg-olive-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Star className="h-6 w-6 text-amber-500 fill-amber-500" />
              <h2 className="text-3xl font-bold text-darkslate-700">
                Our Wishlist Essentials
              </h2>
              <Star className="h-6 w-6 text-amber-500 fill-amber-500" />
            </div>
          </div>
          
          <div className="relative">
            <Button
              onClick={prevSlide}
              variant="outline"
              size="icon"
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-olive-100 border-olive-300 hidden md:flex"
            >
              <ChevronLeft className="h-6 w-6 text-olive-600" />
            </Button>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-8 max-w-3xl mx-auto">
              {currentItems.map((item, index) => (
                <Card key={index} className="border-2 border-olive-300 bg-white hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Star className="h-5 w-5 text-amber-500 fill-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-darkslate-700 text-sm leading-snug mb-1">{item.item}</p>
                        <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button
              onClick={nextSlide}
              variant="outline"
              size="icon"
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-olive-100 border-olive-300 hidden md:flex"
            >
              <ChevronRight className="h-6 w-6 text-olive-600" />
            </Button>
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentSlide === index ? 'bg-olive-500 w-6' : 'bg-olive-300'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Donation Categories */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {donationItems.map((category, index) => {
              const IconComponent = categoryIcons[category.category] || Package;
              const colors = categoryColors[category.category] || { bg: 'bg-mauve-100', text: 'text-darkslate-600', icon: 'text-darkslate-600' };
              return (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border-2 rounded-lg px-6 bg-white hover:border-olive-400 transition-colors"
                >
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
                        <IconComponent className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      <span className="text-xl font-semibold text-darkslate-700">
                        {category.category}
                      </span>
                      <Badge variant="secondary" className="ml-2">
                        {category.items.length} items
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-6">
                    <div className="grid sm:grid-cols-2 gap-3 mt-4">
                      {category.items.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-start p-3 bg-mauve-50 rounded-lg hover:bg-olive-50 transition-colors"
                        >
                          <span className="text-olive-500 mr-2 mt-0.5">✓</span>
                          <span className="text-darkslate-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-12 px-4 bg-mauve-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-mauve-200">
              <CardHeader>
                <CardTitle className="text-lg text-darkslate-700">New or Gently Used</CardTitle>
              </CardHeader>
              <CardContent className="text-darkslate-500">
                We accept both new and gently used items in good condition. Every donation helps a local pet stay home.
              </CardContent>
            </Card>

            <Card className="border-2 border-mauve-200" data-testid="monetary-donations-card">
              <CardHeader>
                <CardTitle className="text-lg text-darkslate-700">Monetary Donations</CardTitle>
              </CardHeader>
              <CardContent className="text-darkslate-500">
                Financial contributions and gift cards to pet stores allow us to purchase exactly what's needed most. Donations can be made via e-transfer to beahero@bitsofkindness.ca
              </CardContent>
            </Card>

            <Card className="border-2 border-mauve-200" data-testid="kbk-basket-shop-card">
              <CardHeader>
                <CardTitle className="text-lg text-darkslate-700">KBK Basket Shop</CardTitle>
              </CardHeader>
              <CardContent className="text-darkslate-500">
                Support pets in a fun way! Visit our <a href="/shop-wishlist" className="text-red-600 hover:text-red-700 font-semibold underline">KBK Basket Shop</a> to select a basket to donate. Every donation goes directly to helping the animals.
              </CardContent>
            </Card>

            <Card className="border-2 border-mauve-200">
              <CardHeader>
                <CardTitle className="text-lg text-darkslate-700">Drop-Off Information</CardTitle>
              </CardHeader>
              <CardContent className="text-darkslate-500">
                Please fill out the donation form above to arrange donation drop-off or pick up. We'll coordinate a convenient time.
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
